import { Badge, mergeClasses } from '@fluentui/react-components';
import { Code16Regular, Sparkle16Regular } from '@fluentui/react-icons';

import { graderTypeLabel } from '../../../services/grader.interface.js';
import { useStyles as usePageStyles } from '../GraderPage.style';

import type { GraderListItemProps } from './GraderListItem.interface';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function formatDate(iso: string, includeYear = false): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(includeYear ? { year: 'numeric' } : {}),
  });
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export function GraderListItem({
  judge,
  isActive,
  onSelect,
}: GraderListItemProps) {
  const pageStyles = usePageStyles();
  const isTypeScript = judge.graderType === 'TypeScriptCustomGraderSpec';
  const isLlm = judge.graderType === 'LlmGraderSpec';

  return (
    <div
      className={mergeClasses(
        pageStyles.judgeListItem,
        isActive && pageStyles.judgeListItemActive,
      )}
      onClick={() => onSelect(judge.id)}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect(judge.id);
        }
      }}
    >
      <div className={pageStyles.judgeListItemHeader}>
        <span className={pageStyles.judgeListItemName}>
          {judge.displayName ?? judge.name}
        </span>
        {judge.graderType && (
          <Badge
            className={pageStyles.judgeListItemType}
            appearance={isTypeScript || isLlm ? 'tint' : 'outline'}
            color={isTypeScript ? 'brand' : 'informative'}
            size="small"
            icon={
              isTypeScript ? (
                <Code16Regular />
              ) : isLlm ? (
                <Sparkle16Regular />
              ) : undefined
            }
            title={graderTypeLabel(judge.graderType)}
            aria-label={`Type: ${graderTypeLabel(judge.graderType)}`}
          >
            {isTypeScript
              ? 'TypeScript'
              : isLlm
                ? 'LLM'
                : graderTypeLabel(judge.graderType)}
          </Badge>
        )}
      </div>
      <div className={pageStyles.judgeListItemMeta}>
        {judge.sourceCheckedInAt ? (
          <span>
            Last Modified on {formatDate(judge.sourceCheckedInAt, true)}
          </span>
        ) : judge.createdAt ? (
          <span>Created {formatDate(judge.createdAt)}</span>
        ) : null}
      </div>
    </div>
  );
}
